import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, User, Clock, CheckCircle, XCircle, AlertCircle, Download, Mail, Phone, MapPin, Video, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';
import { formatearSoloFechaCaracas, formatearFechaHoraCaracas } from '../utils/dateUtils';
import { DocumentosPagoSection, FechaPagoSection } from '../components/GestionDocumentosPago';
import DocumentosSection from '../components/DocumentosSection';
import CentroComunicacionesModal from '../components/CentroComunicacionesModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_CONFIG = {
  solicitud_iniciada: { label: 'Solicitud Iniciada', color: 'bg-blue-500', icon: Clock },
  formulario_1_pendiente: { label: 'Form. 1 Pendiente', color: 'bg-blue-500', icon: FileText },
  cita_pendiente: { label: 'Cita Pendiente', color: 'bg-blue-500', icon: Clock },
  cita_agendada: { label: 'Cita Agendada', color: 'bg-yellow-500', icon: CheckCircle },
  en_evaluacion: { label: 'En Evaluación', color: 'bg-yellow-500', icon: AlertCircle },
  cliente_retenido: { label: 'Cliente Retenido ✓', color: 'bg-green-500', icon: CheckCircle },
  retiro_confirmado: { label: 'Retiro Confirmado', color: 'bg-orange-500', icon: AlertCircle },
  formulario_2_pendiente: { label: 'Form. 2 Pendiente', color: 'bg-blue-500', icon: FileText },
  calculo_pendiente: { label: 'Cálculo Pendiente', color: 'bg-yellow-500', icon: Clock },
  firma_pendiente: { label: 'Firma Pendiente', color: 'bg-orange-500', icon: FileText },
  pago_autorizado: { label: 'Pago Autorizado', color: 'bg-orange-500', icon: CheckCircle },
  completado: { label: 'Completado', color: 'bg-green-500', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-red-500', icon: XCircle }
};

export default function ExpedienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expediente, setExpediente] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showComunicacionesModal, setShowComunicacionesModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUser();
    loadExpediente();
    loadChecklist();
  }, [id]);

  const loadUser = async () => {
    // Intentar obtener rol del localStorage primero para evitar 401 innecesarios
    try {
      const cachedUser = localStorage.getItem('kompa_user');
      if (cachedUser) {
        const parsed = JSON.parse(cachedUser);
        if (parsed.role) {
          setUserRole(parsed.role);
          return;
        }
      }
    } catch (e) { /* ignore parse errors */ }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadExpediente = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/expedientes/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExpediente(data);
      } else {
        toast.error('Expediente no encontrado');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar expediente');
    } finally {
      setLoading(false);
    }
  };


  const loadChecklist = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/meeting-checklists/${id}`, {
        credentials: 'include'
      });
      
      // FIX: No clonar, solo leer una vez
      if (response.ok) {
        const data = await response.json();
        setChecklist(data);
      } else if (response.status === 404) {
        // Es normal que no exista checklist todavía
        console.log('No hay checklist para este expediente aún');
      }
    } catch (error) {
      // Silenciar error si no hay checklist
      console.log('Error loading checklist (expected if none exists):', error);
    }
  };


  const handleDeleteExpediente = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/expedientes/${id}/eliminar`, {
        method: 'DELETE',
        credentials: 'include'
      });

      // FIX: No clonar, solo leer una vez
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Expediente eliminado exitosamente');
        setShowDeleteModal(false);
        navigate('/dashboard');
      } else {
        // Manejar errores específicos
        let errorMessage = 'Error al eliminar expediente';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Si no es JSON, usar mensaje genérico
          if (response.status === 403) {
            errorMessage = 'No tienes permisos para eliminar expedientes. Solo superadmin y gerencial pueden realizar esta acción.';
          } else if (response.status === 404) {
            errorMessage = 'Expediente no encontrado';
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al eliminar expediente');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // handleResendEmail eliminada - ahora se usa Centro de Comunicaciones Modal


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando expediente...</p>
        </div>
      </div>
    );
  }

  if (!expediente) return null;

  const config = ESTADO_CONFIG[expediente.estado];
  // Override inteligente del badge: si calculo existe pero estado sigue en calculo_pendiente
  let badgeLabel = config?.label;
  let badgeColor = config?.color;
  if (expediente.estado === 'calculo_pendiente' && expediente.calculo?.documento_url) {
    badgeLabel = 'Cálculo Cargado';
    badgeColor = 'bg-green-500';
  }
  const IconComponent = config?.icon || AlertCircle;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <img src="/kompa-logo.png" alt="Kompa" className="h-10 w-auto" />
          </div>
          <Badge className={`${badgeColor} text-white flex items-center gap-2 px-4 py-2`}>
            <IconComponent className="w-5 h-5" />
            {badgeLabel}
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Título */}
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Expediente {expediente.numero_expediente}
          </h1>
          <p className="text-gray-400">
            Creado el {formatearSoloFechaCaracas(expediente.created_at)}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna Izquierda */}
          <div className="lg:col-span-2 space-y-6">
            {/* Información del Solicitante */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-yellow-500" />
                  Información del Solicitante
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem label="Nombre Completo" value={expediente.solicitante.nombre_completo} />
                  <InfoItem label="Cédula" value={expediente.solicitante.cedula} />
                  <InfoItem 
                    label="Email" 
                    value={expediente.solicitante.email} 
                    icon={<Mail className="w-4 h-4" />}
                  />
                  <InfoItem 
                    label="Teléfono" 
                    value={expediente.solicitante.telefono} 
                    icon={<Phone className="w-4 h-4" />}
                  />
                </div>
                <InfoItem 
                  label="Dirección" 
                  value={expediente.solicitante.direccion} 
                  icon={<MapPin className="w-4 h-4" />}
                  fullWidth
                />
              </CardContent>
            </Card>

            {/* Motivo del Retiro */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Motivo del Retiro</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 leading-relaxed">{expediente.motivo_retiro}</p>
              </CardContent>
            </Card>

            {/* Documentos Consolidados - NUEVO COMPONENTE */}
            <DocumentosSection
              expediente={expediente}
              expedienteId={id}
              userRole={userRole}
              onUpdate={loadExpediente}
            />

            {/* Formulario 2 - Información Adicional */}
            {expediente.formulario_2_data && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-500" />
                    Formulario 2 - Información Adicional
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Motivo Detallado del Retiro:</p>
                    <p className="text-gray-300 leading-relaxed bg-gray-900/50 p-3 rounded-lg">
                      {expediente.formulario_2_data.motivo_retiro_detallado}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Información de Cita */}
            {expediente.cita && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-yellow-500" />
                    Información de la Cita
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <InfoItem 
                    label="Fecha" 
                    value={formatearFechaHoraCaracas(expediente.cita.fecha)}
                  />
                  <InfoItem 
                    label="Tipo" 
                    value={expediente.cita.tipo === 'virtual' ? 'Virtual (Meet)' : 'Presencial'}
                  />
                  {expediente.cita.meet_link && (
                    <div>
                      <a 
                        href={expediente.cita.meet_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-yellow-500 hover:text-yellow-400 flex items-center gap-2"
                      >
                        <Calendar className="w-4 h-4" />
                        Abrir reunión de Meet
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notas de Reunión */}
            {expediente.notas_reunion && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Notas de la Reunión</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 leading-relaxed">{expediente.notas_reunion}</p>
                </CardContent>
              </Card>
            )}


            {/* Grabación de la Reunión */}
            {(checklist?.video_url || expediente.video_llamada_url) && (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Video className="w-5 h-5 text-purple-500" />
                    Grabación de la Reunión
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-gray-900/50 rounded-lg overflow-hidden">
                    <video
                      controls
                      className="w-full rounded-lg"
                      src={checklist?.video_url || expediente.video_llamada_url}
                      data-testid="video-reunion-player"
                    >
                      Tu navegador no soporta el elemento de video.
                    </video>
                  </div>
                  <div className="flex gap-3">
                    <a
                      href={checklist?.video_url || expediente.video_llamada_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-400 hover:text-blue-300 underline"
                    >
                      Abrir en nueva pestaña
                    </a>
                    <a
                      href={checklist?.video_url || expediente.video_llamada_url}
                      download
                      className="text-sm text-green-400 hover:text-green-300 underline"
                    >
                      Descargar video
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* FASE 3: Documentos de Pago (solo Admin/Gerencial) */}
            {(userRole === 'admin' || userRole === 'gerencial') && 
             (expediente.estado === 'formulario_2_pendiente' || 
              expediente.estado === 'calculo_pendiente' || 
              expediente.estado === 'firma_pendiente' || 
              expediente.estado === 'pago_autorizado') ? (
              <DocumentosPagoSection 
                expedienteId={id}
                expediente={expediente}
                onUpdate={loadExpediente}
              />
            ) : null}

            {/* FASE 4: Configurar Fecha de Pago (solo Admin/Gerencial cuando calculo existe) */}
            {(userRole === 'admin' || userRole === 'gerencial') && 
             expediente.calculo?.documento_url && 
             (expediente.estado === 'calculo_pendiente' || 
              expediente.estado === 'firma_pendiente' || 
              expediente.estado === 'pago_autorizado') ? (
              <FechaPagoSection 
                expedienteId={id}
                expediente={expediente}
                onUpdate={loadExpediente}
              />
            ) : null}

          </div>

          {/* Columna Derecha */}
          <div className="space-y-6">
            {/* Historial */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  Historial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {expediente.historial && expediente.historial.length > 0 ? (
                    expediente.historial.slice().reverse().map((evento, index) => (
                      <div key={index} className="relative pl-6 pb-4 border-l-2 border-gray-700 last:pb-0">
                        <div className="absolute left-0 top-0 -translate-x-1/2 w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-white">{evento.accion}</p>
                          {evento.usuario_nombre && (
                            <p className="text-xs text-gray-400">Por: {evento.usuario_nombre}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            {formatearFechaHoraCaracas(evento.fecha)}
                          </p>
                          {evento.notas && (
                            <p className="text-xs text-gray-400 mt-1">{evento.notas}</p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No hay historial disponible</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Acciones Rápidas */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Acciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                  onClick={() => setShowComunicacionesModal(true)}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  📨 Centro de Comunicaciones
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Expediente
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Modal Centro de Comunicaciones */}
      <CentroComunicacionesModal
        isOpen={showComunicacionesModal}
        onClose={() => setShowComunicacionesModal(false)}
        expedienteId={id}
        destinatario={expediente?.solicitante?.email}
        onSuccess={loadExpediente}
      />

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteExpediente}
        title="Eliminar Expediente"
        message={
          <div className="space-y-3">
            <p className="text-gray-300">
              ¿Estás seguro de que deseas eliminar este expediente?
            </p>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-300 text-sm font-semibold">
                ⚠️ Esta acción no se puede deshacer
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <p className="text-white text-sm"><strong>Expediente:</strong> {expediente?.numero_expediente}</p>
              <p className="text-white text-sm"><strong>Cliente:</strong> {expediente?.solicitante.nombre_completo}</p>
            </div>
          </div>
        }
        confirmText={deleting ? "Eliminando..." : "Sí, Eliminar"}
        cancelText="Cancelar"
        isDangerous={true}
      />
    </div>
  );
}

function InfoItem({ label, value, icon, fullWidth = false }) {
  return (
    <div className={fullWidth ? 'col-span-full' : ''}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        {icon && <span className="text-yellow-500">{icon}</span>}
        <p className="text-white font-medium">{value || 'N/A'}</p>
      </div>
    </div>
  );
}
